import React, { useEffect, useRef, useState } from 'react';
import Rodal from '../../../../components/Rodal/Rodal';
import './EngagementDocumentUploadModal.scss';
import { useStateSelector } from '../../../../store/selectors';
import { ModalBody, ModalHeader } from 'reactstrap';
import { FileSelector } from '../../../../components/FileSelector/FileSelector';
import {
    ActionDocumentType,
    EngagementDocumentTypeOptionsMapping,
} from '../../../../slices/marketplace/timeline/models/enums/action-document-type.enum';
import { Formik } from 'formik';
import createNotification from '../../../../utils/createNotification';
import ButtonLoader from '../../../../components/Layout/Buttons/ButtonLoader';
import DocumentsHistoryPopup from '../../../AdminMarketplace/DocumetsHistoryPopup/DocumetsHistoryPopup';
import { EngagementDocumentSource } from '../../../../slices/marketplace/timeline/models/enums/engagement-document-source';
import { DocumentDetailsStatus } from '../../../../slices/marketplace/timeline/models/enums/document-details-status.enum';
import {
    DocumentDetailsContentSubtype,
    DocumentDetailsContentSubtypeEnumToStringMapping as subTypeEnumToStringMap,
} from '../../../../slices/marketplace/timeline/models/enums/document-details-content-subtype';
import { DocumentDetails } from '../../../../slices/marketplace/timeline/models/document-details.model';
import { TimelineEvent } from '../../../../slices/marketplace/timeline/models/timeline-event.model';

interface EngagementDocumentUploadModalProps {
    isOpen: boolean;
    onClose: () => void;
    onUpload: () => void;
    documentId: string;
}

const EngagementDocumentUploadModal = (
    props: EngagementDocumentUploadModalProps
) => {
    const formRef = useRef<any>(null);
    const axios = useStateSelector((state) => state.core.axios);

    const doc = useStateSelector((state) =>
        state.timelines.currentTimeline.engagementData.documentsList.find(
            (doc) => doc.id === props.documentId
        )
    );
    const currentTimeline = useStateSelector(
        (state) => state.timelines.currentTimeline
    );

    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [isFileUploading, setIsFileUploading] = useState(false);
    const [isDocumentsHistoryPopupVisible, setIsDocumentsHistoryPopupVisible] =
        useState(false);
    useEffect(() => {
        document.body.style.overflow = 'hidden';

        return () => {
            document.body.style.overflow = 'unset';
        };
    }, []);

    const uploadFile = () => {
        if (selectedFile) {
            setIsFileUploading(true);
            const formData = new FormData();
            formData.append('file', selectedFile);
            axios
                .post(
                    `/api/marketplace/engagements/${currentTimeline.id}/documents/${props.documentId}`,
                    formData
                )
                .then((response) => {
                    if (response.status === 200) {
                        createNotification(
                            'File uploaded successfully',
                            'success'
                        );
                        props.onUpload();
                        return;
                    }

                    createNotification(
                        'Some unexpected error occurred while uploading file.',
                        'error'
                    );
                })
                .catch(() => {
                    createNotification(
                        'Some unexpected error occurred while uploading file.',
                        'error'
                    );
                })
                .finally(() => {
                    setIsFileUploading(false);
                });
        }
    };

    const downloadFile = (documentId: string = undefined) => {
        const urlToFetchLink = `/api/marketplace/engagements/${
            currentTimeline.id
        }/documentsDetails/${documentId ?? props.documentId}`;
        axios
            .get(urlToFetchLink)
            .then((response: any) => {
                window.open(response.data, '_blank');
            })
            .catch(() =>
                createNotification(
                    'Some unexpected error occured while downloading file.',
                    'error'
                )
            );
    };

    const getDocumentEvent = () => {
        let docIdToFindBy = doc.id;

        if (
            !!doc?.contentSubtype &&
            doc?.contentSubtype != DocumentDetailsContentSubtype.Blinded
        ) {
            const masterOfDoc =
                currentTimeline.engagementData.documentsList.find(
                    (d) => d.id === doc.masterDocumentId
                );

            if (!!masterOfDoc) docIdToFindBy = masterOfDoc.id;
        }

        const foundEvent = currentTimeline.events.find(
            (e) => e.linkedAction?.referenceItemId === docIdToFindBy
        );

        return foundEvent;
    };

    const getOldReferenceIdsList = (foundEvent?: TimelineEvent) => {
        const event = foundEvent ?? getDocumentEvent();

        if (
            !doc?.contentSubtype ||
            doc?.contentSubtype == DocumentDetailsContentSubtype.Blinded
        ) {
            return event?.linkedAction?.oldReferenceItemIds;
        } else {
            const subtypeOldRefIds = !event?.linkedAction
                ?.oldReferenceItemAdditionalVersionIds
                ? []
                : event?.linkedAction?.oldReferenceItemAdditionalVersionIds[
                      subTypeEnumToStringMap[doc.contentSubtype]
                  ];
            return subtypeOldRefIds;
        }
    };

    const filterDocumentDetails = () => {
        let docDetailsList: DocumentDetails[] = [];
        const event = getDocumentEvent();

        if (
            !doc?.contentSubtype ||
            doc?.contentSubtype == DocumentDetailsContentSubtype.Blinded
        ) {
            docDetailsList =
                currentTimeline.engagementData.documentsList.filter(
                    (d) =>
                        (d.source === EngagementDocumentSource.AdminUpload ||
                            d.source ===
                                EngagementDocumentSource.BrokerUpload) &&
                        (event?.linkedAction?.referenceItemId == d.id ||
                            event?.linkedAction?.oldReferenceItemIds?.includes(
                                d.id
                            ))
                );
        } else {
            const masterOfDoc =
                currentTimeline.engagementData.documentsList.find(
                    (d) => d.id === doc.masterDocumentId
                );
            const currentAdditionalIds = masterOfDoc?.additionalVersionIds;
            const oldAddRefIds = getOldReferenceIdsList(event);
            docDetailsList =
                currentTimeline.engagementData.documentsList.filter(
                    (d) =>
                        (d.source === EngagementDocumentSource.AdminUpload ||
                            d.source ===
                                EngagementDocumentSource.BrokerUpload) &&
                        d.contentSubtype == doc?.contentSubtype &&
                        d.id != d.masterDocumentId &&
                        (currentAdditionalIds.includes(d.id) ||
                            oldAddRefIds.includes(d.id))
                );
        }

        docDetailsList.sort((a, b) => {
            return new Date(a.datePosted) > new Date(b.datePosted) ? -1 : 1;
        });
        return docDetailsList;
    };

    return (
        <>
            <Rodal
                className="engagement-document-upload-modal"
                visible={props.isOpen}
                onClose={props.onClose}>
                <ModalHeader>Upload Engagement Document</ModalHeader>
                <ModalBody>
                    <Formik
                        innerRef={formRef}
                        initialValues={{
                            sourceFile: doc.fileName
                                ? doc.fileName.replace(/(_\d+)(\.[^.]+)$/, '$2')
                                : '',
                        }}
                        onSubmit={() => {}}>
                        {({ values }) => (
                            <div>
                                <FileSelector
                                    extension={
                                        EngagementDocumentTypeOptionsMapping.find(
                                            (o) => Number(doc.type) === o.value
                                        ).fileExtensions
                                    }
                                    disabled={
                                        doc?.type ===
                                            ActionDocumentType.DocumentWithFeedback &&
                                        doc?.status ===
                                            DocumentDetailsStatus.Completed
                                    }
                                    file={selectedFile}
                                    setFile={(file: File) => {
                                        setSelectedFile(file);
                                        formRef.current.setFieldValue(
                                            'fileName',
                                            file?.name,
                                            true
                                        );
                                        formRef.current.setFieldValue(
                                            'sourceFile',
                                            file?.name,
                                            true
                                        );
                                    }}
                                    fileName={
                                        selectedFile
                                            ? selectedFile.name
                                            : values.sourceFile
                                    }
                                    downloadFile={downloadFile}
                                    setIsDocumentsHistoryPopupVisible={
                                        setIsDocumentsHistoryPopupVisible
                                    }
                                    oldDocumentDetailsList={getOldReferenceIdsList()}
                                />
                                <div className="buttons-wrapper">
                                    <button
                                        disabled={false}
                                        className="btn btn-outline-secondary"
                                        onClick={props.onClose}>
                                        {'Cancel'}
                                    </button>
                                    <ButtonLoader
                                        disabled={!selectedFile}
                                        onClick={uploadFile}
                                        className={'btn btn-primary'}
                                        buttonText={'Upload File'}
                                        isLoading={isFileUploading}
                                        loaderButtonText={'Upload File'}
                                    />
                                </div>
                            </div>
                        )}
                    </Formik>
                </ModalBody>
            </Rodal>
            {isDocumentsHistoryPopupVisible && (
                <DocumentsHistoryPopup
                    downloadDocument={downloadFile}
                    oldDocumentDetailsList={filterDocumentDetails()}
                    isVisible={isDocumentsHistoryPopupVisible}
                    onClose={() => setIsDocumentsHistoryPopupVisible(false)}
                />
            )}
        </>
    );
};

export default EngagementDocumentUploadModal;
